/**
 * Planned sales as CashflowCalculator books them: legacy properties and depreciating assets,
 * and unified assets. Every sale settles its linked debts and clears what they owe (R2, R9);
 * a legacy record without linked debts clears its embedded mortgage or finance instead.
 */
import { minDay, monthsBetween, type Day } from '../core/calendar.ts';
import { powDouble } from '../core/pow.ts';
import { abs, dec, div, fromDouble, gt, max, mul, sub, sum, toDouble, ZERO, type Decimal } from '../core/decimal.ts';
import { assetValue } from '../models/asset.ts';
import { depreciatedValueAfter, remainingEmbeddedFinance } from '../models/depreciating-asset.ts';
import type { Asset, DepreciatingAsset, Id, Mortgage, Plan, Property, Transaction } from '../types/domain.ts';
import { appreciateOverWholeMonths } from './appreciation.ts';
import { remainingBalance } from './debt-projector.ts';

export interface SaleBreakdown {
  readonly projectedValue: Decimal;
  readonly salePrice: Decimal;
  readonly sellingCosts: Decimal;
  readonly debtCleared: Decimal;
  readonly netProceeds: Decimal;
}

/** SaleBreakdown.init: price = explicit ?? projected; costs = price × (pct/100); proceeds floored at 0. */
export function saleBreakdown(
  projectedValue: Decimal,
  explicitPrice: Decimal | null,
  sellingCostsPercent: Decimal,
  debtCleared: Decimal,
): SaleBreakdown {
  const salePrice = explicitPrice ?? projectedValue;
  const sellingCosts = mul(salePrice, div(sellingCostsPercent, dec(100)));
  const netProceeds = max(sub(sub(salePrice, sellingCosts), debtCleared), ZERO);
  return { projectedValue, salePrice, sellingCosts, debtCleared, netProceeds };
}

/**
 * Mortgage left at sale (D8c): P((1+r)^n − (1+r)^t)/((1+r)^n − 1) in Double with
 * t = min(monthsOwned, n) (not floored at 0); the principal when interest-only, r ≤ 0 or n ≤ 0.
 */
export function mortgageLeftAtSale(mortgage: Mortgage, monthsOwned: number): Decimal {
  if (mortgage.isInterestOnly) return mortgage.loanAmount;
  const monthlyRate = div(div(mortgage.interestRate, dec(100)), dec(12));
  const termMonths = mortgage.termYears * 12;
  if (!gt(monthlyRate, ZERO) || termMonths <= 0) return mortgage.loanAmount;

  const r = toDouble(monthlyRate);
  const toN = powDouble(1 + r, termMonths);
  const toT = powDouble(1 + r, Math.min(monthsOwned, termMonths));
  return max(fromDouble((toDouble(mortgage.loanAmount) * (toN - toT)) / (toN - 1)), ZERO);
}

/** What `debts` still owe on `date`, as a positive amount: the loan a sale clears. */
const owedOn = (debts: readonly Transaction[], date: Day, now: Day): Decimal =>
  sum(debts.map((debt) => abs(remainingBalance(debt, date, now))));

/**
 * CashflowCalculator.propertySale: value appreciated over whole months (R5), less what its
 * linked debts owe at the sale date, or the embedded mortgage (mortgageLeftAtSale) when it has
 * none (R9).
 */
export function propertySale(property: Property, saleDate: Day, linkedDebts: readonly Transaction[], now: Day): SaleBreakdown | null {
  const sale = property.saleEvent;
  if (sale === null) return null;
  const monthsOwned = monthsBetween(property.startDate, saleDate);
  return saleBreakdown(
    appreciateOverWholeMonths(property.propertyValue, property.annualAppreciationRate, monthsOwned),
    sale.salePrice,
    sale.sellingCostsPercent,
    linkedDebts.length === 0 ? mortgageLeftAtSale(property.mortgage, monthsOwned) : owedOn(linkedDebts, saleDate, now),
  );
}

/**
 * CashflowCalculator.assetSale: fractional-year depreciation (D2b), less what its linked debts
 * owe at the sale date, or the embedded loan finance when it has none (R9).
 */
export function assetSale(asset: DepreciatingAsset, saleDate: Day, linkedDebts: readonly Transaction[], now: Day): SaleBreakdown | null {
  const sale = asset.saleEvent;
  if (sale === null) return null;
  const monthsOwned = monthsBetween(asset.purchaseDate, saleDate);
  return saleBreakdown(
    depreciatedValueAfter(asset, monthsOwned),
    sale.expectedPrice,
    sale.sellingCostsPercent,
    linkedDebts.length === 0 ? remainingEmbeddedFinance(asset, monthsOwned) : owedOn(linkedDebts, saleDate, now),
  );
}

/**
 * CashflowCalculator.unifiedAssetSale: Asset.value at the sale date (or the expected price),
 * selling costs, and Σ |remaining balance| of its linked debts at the sale date, which the sale
 * clears (R2).
 */
export function unifiedAssetSale(asset: Asset, linkedDebts: readonly Transaction[], now: Day): SaleBreakdown | null {
  const sale = asset.saleEvent;
  if (sale === null) return null;
  return saleBreakdown(assetValue(asset, sale.date), sale.expectedPrice, sale.sellingCostsPercent, owedOn(linkedDebts, sale.date, now));
}

/**
 * AssetSales.saleDates: the planned sale date of every active legacy property, legacy
 * depreciating asset and unified asset that is not also a legacy record, keyed by id (the first
 * listed wins a shared id).
 */
export function saleDates(
  assets: readonly Asset[],
  properties: readonly Property[],
  depreciatingAssets: readonly DepreciatingAsset[],
): ReadonlyMap<Id, Day> {
  const legacyIds = new Set([...properties.map((p) => p.id), ...depreciatingAssets.map((a) => a.id)]);
  const result = new Map<Id, Day>();
  const note = (id: Id, sale: { readonly date: Day } | null) => {
    if (sale !== null && !result.has(id)) result.set(id, sale.date);
  };
  for (const p of properties) if (p.isActive) note(p.id, p.saleEvent);
  for (const a of depreciatingAssets) if (a.isActive) note(a.id, a.saleEvent);
  for (const a of assets) if (a.isActive && !legacyIds.has(a.id)) note(a.id, a.saleEvent);
  return result;
}

/** Plan.saleDates. */
export const planSaleDates = (plan: Plan): ReadonlyMap<Id, Day> => saleDates(plan.assets, plan.properties, plan.depreciatingAssets);

/** AssetSales.settlements: active debts linked to an asset with a planned sale, keyed by debt id, with that sale's date. */
export function saleSettlements(dates: ReadonlyMap<Id, Day>, transactions: readonly Transaction[]): ReadonlyMap<Id, Day> {
  const settlements = new Map<Id, Day>();
  for (const debt of transactions) {
    const saleDate = debt.linkedAssetId === null ? undefined : dates.get(debt.linkedAssetId);
    if (debt.type === 'debt' && debt.isActive && saleDate !== undefined) settlements.set(debt.id, saleDate);
  }
  return settlements;
}

/**
 * AssetSales.settled: each settled debt's payments end on its asset's sale date. `onlyDebts`
 * mirrors the grouped overload, which rewrites only the debt group; the flat one rewrites any
 * transaction sharing a settled debt's id.
 */
export function settleSoldAssetDebts(
  transactions: readonly Transaction[],
  dates: ReadonlyMap<Id, Day>,
  { onlyDebts }: { readonly onlyDebts: boolean },
): readonly Transaction[] {
  const settlements = saleSettlements(dates, transactions);
  if (settlements.size === 0) return transactions;
  return transactions.map((t) => {
    const saleDate = settlements.get(t.id);
    if (saleDate === undefined || (onlyDebts && t.type !== 'debt')) return t;
    return { ...t, endDate: minDay(t.endDate ?? saleDate, saleDate) };
  });
}
